import { useEffect, useState } from 'react';
import { DateValue, Heading } from 'react-aria-components';

import { parseDate } from '@internationalized/date';
import { useAtom } from 'jotai';

import { getMyAttendanceReservations } from '@/api/getAttendanceReservations';
import { getChildrenByLineID } from '@/api/getChildByLineID';
import Button from '@/components/Button/Button';
import { AttendanceReservationFormDialog } from '@/components/reservations/line/AttendanceReservationFormDialog';
import MobileMonthlyCalendar from '@/components/reservations/line/MobileMonthlyCalendar';
import { LineUserAtom } from '@/jotai/jotai';
import { formatDate, formattedDate } from '@/logics/date/date';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';
import { Child } from '@/types/child';

export const Page = () => {
  const [lineUser, _] = useAtom(LineUserAtom);
  const [child, setChild] = useState<Child>();
  const [focusedDate, setFocusedDate] = useState<DateValue>(parseDate(formatDate(new Date())));
  const [selectedDate, setSelectedDate] = useState<DateValue>();
  const [targetReservation, setTargetReservation] = useState<DetailedAttendanceReservation>();
  const [displayDate, setDisplayDate] = useState<string>();
  const [isOpenCreate, setIsOpenCreate] = useState<boolean>(false);
  const [reservations, setReservations] = useState<DetailedAttendanceReservation[]>([]);

  useEffect(() => {
    setDisplayDate(formattedDate(selectedDate?.toDate('Asia/Tokyo') || new Date(), 'yyyy年M月'));
  }, []);

  useEffect(() => {
    const getChild = async () => {
      const response = await getChildrenByLineID(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? ''
      );
      if (response.status === 200) {
        setChild(response.result[0]);
      }
    };

    if (lineUser) {
      getChild();
    }
  }, [lineUser]);

  useEffect(() => {
    const fetchReservation = async () => {
      const res = await getMyAttendanceReservations(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        child?.id ?? 0,
        focusedDate?.year.toString() ?? new Date().getFullYear().toString(),
        focusedDate?.month.toString() ?? new Date().getMonth().toString()
      );
      if (res.status === 200) {
        setReservations(res.result);
      }
    };
    if (child && lineUser) {
      fetchReservation();
    }
  }, [child, focusedDate]);

  useEffect(() => {
    if (selectedDate) {
      setTargetReservation(
        reservations.find((res) => res.date === formatDate(selectedDate.toDate('Asia/Tokyo')))
      );
    }
  }, [selectedDate]);

  return (
    <div className="m-4 space-y-4">
      <Heading className="text-2xl font-bold">来所予約</Heading>

      <div className="rounded-8 bg-blue-100 px-2 py-3">
        <Heading className="text-lg font-bold">
          {`${child?.last_name || ''} ${child?.first_name || ''}`}
        </Heading>
        <Heading className="text-lg font-bold">{displayDate}の予定</Heading>
      </div>

      <MobileMonthlyCalendar
        reservations={reservations}
        setSelectedDate={setSelectedDate}
        setFocusedDate={setFocusedDate}
      />

      <div className="rounded-md bg-gray-100 px-2 py-4">
        <div className="flex justify-between">
          <Heading className="text-lg font-bold">
            {selectedDate
              ? `${formattedDate(selectedDate.toDate('Asia/Tokyo'), 'yyyy年MM月dd日（E）')}`
              : '予定を選択してください'}
          </Heading>
          {selectedDate && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => setIsOpenCreate(true)}
            >
              {targetReservation ? '出欠編集' : '出欠入力'}
            </Button>
          )}
        </div>
        <div className="mt-4">
          {selectedDate && targetReservation && (
            <div>
              {targetReservation.attendance_status === 'attend'
                ? '出席で予約されています'
                : '欠席で予約されています'}
            </div>
          )}
        </div>
      </div>
      {isOpenCreate && (
        <AttendanceReservationFormDialog
          selectedDate={selectedDate}
          child={child}
          isOpen={isOpenCreate}
          setIsOpen={setIsOpenCreate}
          resID={targetReservation?.id}
          afterSubmit={() => {
            // 予約を更新した後の処理
            setIsOpenCreate(false);
            setFocusedDate(parseDate(formatDate(selectedDate?.toDate('Asia/Tokyo') || new Date())));
          }}
        />
      )}
    </div>
  );
};
